import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon } from 'lucide-react';
export interface StrategyStep {
  /** Compact stap-label, bv. "48 + 40" of "rond af op 50" */
  label: string;
  /** Optionele didactische toelichting bij de stap */
  description?: string;
  /** Markeer deze stap visueel (de "kern-stap" van de strategie) */
  highlight?: boolean;
}
interface StrategyStepsProps {
  /** Geordende reeks strategie-stappen (minimaal 2) */
  steps: StrategyStep[];
  /** Titel boven de stappen, bv. "Compenseren" of "Splitsen" */
  title?: string;
  /** Override label uit de seed (vervangt title in de header) */
  label?: string;
  /** Render-context — bepaalt typografische dichtheid */
  mode?: 'stimulus' | 'support';
}
/**
 * StrategySteps — visualiseert een handig-rekenen strategie als
 * genummerde stappen met pijlen ertussen. Bedoeld als support-visual
 * voor families als ^REK-HANDIG-PLUS-MIN / ^REK-HANDIG-KEER waar het
 * kind de strategie-naam aan een concrete reeks moet koppelen.
 *
 * Didactisch verschilt het van RuleCard (vrije tekstregel) en
 * CompensationNumberLine (één specifieke strategie op de getallenlijn):
 * StrategySteps is een generieke, herbruikbare "stappen-keten" die
 * elke strategie-vorm kan dragen (splitsen, compenseren, rijgen,
 * verdubbelen/halveren) zonder een nummerlijn nodig te hebben.
 */
export function StrategySteps({
  steps,
  title,
  label,
  mode = 'support'
}: StrategyStepsProps) {
  const isStimulus = mode === 'stimulus';
  const heading = label ?? title;
  if (!steps || steps.length === 0) return null;
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className={`bg-indigo-50 rounded-xl border border-indigo-100 ${isStimulus ? 'p-3.5' : 'p-4'}`} aria-label={heading ? `Strategie: ${heading}` : 'Strategie-stappen'} data-id="element-12763">
      {heading && <div className="flex items-center gap-2 mb-3" data-id="element-12764">
          <div className={`rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0 ${isStimulus ? 'w-7 h-7' : 'w-8 h-8'}`} data-id="element-12765">
            <SparklesIcon className={`text-indigo-700 ${isStimulus ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} data-id="element-12766" />
          </div>
          <h4 className={`font-bold text-indigo-900 ${isStimulus ? 'text-sm' : 'text-base'}`} data-id="element-12767">
            {heading}
          </h4>
        </div>}

      <ol className="flex flex-col gap-2" aria-label="Geordende reeks strategie-stappen" data-id="element-12768">
        {steps.map((step, idx) => {
        const isLast = idx === steps.length - 1;
        const isHighlight = step.highlight === true;
        return <motion.li key={idx} initial={{
          opacity: 0,
          x: -4
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.05 + idx * 0.05,
          duration: 0.25,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex flex-col" data-id="element-12769">
              <div className="flex items-center gap-2.5" data-id="element-12770">
                <div className={`flex items-center justify-center flex-shrink-0 rounded-full font-bold ${isHighlight ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-indigo-700 border border-indigo-200'} ${isStimulus ? 'w-6 h-6 text-xs' : 'w-7 h-7 text-sm'}`} aria-label={`Stap ${idx + 1}`} data-id="element-12771">
                  {idx + 1}
                </div>
                <div className={`flex-1 rounded-lg px-3 py-1.5 ${isHighlight ? 'bg-indigo-100 border border-indigo-200 shadow-sm' : 'bg-white border border-indigo-100'}`} data-id="element-12772">
                  <div className={`font-semibold text-slate-900 ${isStimulus ? 'text-sm' : 'text-base'}`} data-id="element-12773">
                    {step.label}
                  </div>
                  {step.description && <div className={`text-slate-600 mt-0.5 leading-snug ${isStimulus ? 'text-xs' : 'text-sm'}`} data-id="element-12774">
                      {step.description}
                    </div>}
                </div>
              </div>
              {!isLast && <div className="flex items-center justify-center my-1" aria-hidden="true" data-id="element-12775">
                  <ArrowRightIcon className={`text-indigo-400 rotate-90 ${isStimulus ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} data-id="element-12776" />
                </div>}
            </motion.li>;
      })}
      </ol>
    </motion.div>;
}